import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaChevronDown } from 'react-icons/fa';
import Testimonials from '../components/ui/Testimonials';
import Popup from '../components/ui/Popup';
import WelcomeBanner from '../components/layout/WelcomeBanner';
import StudentTypeCard from '../components/cards/StudentTypeCard';
import CurrentEventsSection from '../components/layout/CurrentEventsSection';
import ElectionsHighlight from '../components/layout/ElectionsHighlight';
import GetConnectedForm from '../components/forms/GetConnectedForm';
import { CURRENT_EVENTS_ENABLED } from '../data/events/currentEvents/currentEventsIndex';
import homepage from '../cms/homepage.json';
import RichText from '../components/ui/RichText';
import '../css/pages/Home.css';

// Homepage content (managed in Sanity → src/cms/homepage.json)
const studentTypes = homepage.studentTypes || [];
const studentTypesInfo = homepage.studentTypesInfo || {};
const featuresData = homepage.features || [];
const featuresTitle = homepage.featuresTitle || 'What We Offer';
const welcomeBannerConfig = homepage.welcomeBanner || {};

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const featuresRef = useRef(null);
  const studentTypesRef = useRef(null);
  const currentEventsRef = useRef(null);
  const [bannerVisible, setBannerVisible] = useState(welcomeBannerConfig.isActive);
  const [showGetConnectedForm, setShowGetConnectedForm] = useState(false);
  const prevConfigRef = useRef(welcomeBannerConfig.isActive);


  // Check config on each render and update if needed
  const currentConfig = welcomeBannerConfig.isActive;
  if (currentConfig !== prevConfigRef.current) {
    prevConfigRef.current = currentConfig;
    setBannerVisible(currentConfig);
  }

  // Determine if banner should actually be rendered
  const shouldRenderBanner = bannerVisible && welcomeBannerConfig.isActive;

  // Handle Get Connected form based on route
  useEffect(() => {
    if (location.pathname === '/get-connected') {
      setShowGetConnectedForm(true);
    } else {
      setShowGetConnectedForm(false);
    }
  }, [location.pathname]);

  // Handle fade-up animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '-50px' }
    );

    const fadeElements = document.querySelectorAll('.fade-up');
    fadeElements.forEach((el) => observer.observe(el));

    return () => {
      fadeElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Reusable scroll function that automatically calculates header height
  const scrollToSection = (ref, customOffset = 0) => {
    if (ref.current) {
      // Dynamically get header height from the actual navbar element
      const header = document.querySelector('header, .header, nav, .navbar');
      const headerHeight = header ? header.offsetHeight : 0;
      
      const top = ref.current.getBoundingClientRect().top + window.scrollY - headerHeight + customOffset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const handleBannerClose = () => {
    setBannerVisible(false);
  };

  const handleGetConnectedClick = () => {
    setShowGetConnectedForm(true);
    navigate('/get-connected');
  };

  const closeGetConnectedForm = () => {
    setShowGetConnectedForm(false);
    navigate('/');
  };

  return (
    <>
      <Popup />
      <main className="main-background">
        <section 
          className={`hero-section ${shouldRenderBanner ? 'with-banner' : ''}`}
          style={{
            '--hero-bg-image': `url("${homepage.heroImageUrl}")`
          }}
        >
          <div className="hero-content">
            <h1 className="hero-title fade-up delay-0">
              {homepage.heroTitleTop}<br />
              <span className="hopkins-blue">{homepage.heroTitleHighlight}</span>
            </h1>
            <p className="hero-subtitle fade-up delay-1">
              <RichText value={homepage.heroSubtitle} className="hero-subtitle" />
            </p>
          </div>
          
          {/* Comment out line below to disable banner. */}
          {shouldRenderBanner && <WelcomeBanner onClose={handleBannerClose} />}

          <button
            className="scroll-indicator"
            aria-label="Scroll to view more"
            onClick={() => scrollToSection(
              CURRENT_EVENTS_ENABLED ? currentEventsRef : studentTypesRef
            )}
          >
            <FaChevronDown size={32} color="#fff" />
            <span className="scroll-text">Scroll to view more</span>
          </button>
        </section>

        <CurrentEventsSection ref={currentEventsRef} />

        <ElectionsHighlight />

        <section className="student-types-section" ref={studentTypesRef}>
          <div className="student-types-container">
            <h2 className="section-title" data-aos="fade-up">{studentTypesInfo.title}</h2>
            <p className="section-subtitle" data-aos="fade-up" data-aos-delay="100">{studentTypesInfo.subtitle}</p>
            <RichText value={studentTypesInfo.description} className="section-description" data-aos="fade-up" data-aos-delay="200" />
            <div className="student-types-grid">
              {studentTypes.map((studentType, index) => (
                <div key={studentType.id} data-aos="fade-up" data-aos-delay={300 + (index * 100)}>
                  <StudentTypeCard 
                    studentType={studentType}
                    onGetConnectedClick={studentType.link === '/get-connected' ? handleGetConnectedClick : undefined}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="features-section" ref={featuresRef}>
          <h2 className="section-title" data-aos="fade-up">{featuresTitle}</h2>
          <div className="features-grid">
            {featuresData.map((feature, index) => (
              <div key={feature.id} className="feature-card" data-aos="fade-up" data-aos-delay={index * 100}>
                <h3>{feature.title}</h3>
                <RichText value={feature.description} />
              </div>
            ))}
          </div>
        </section>

        <Testimonials />
      </main>

      {showGetConnectedForm && <GetConnectedForm onClose={closeGetConnectedForm} />}
    </>
  );
};

export default Home;
 